import { useState } from 'react';
import { Calculator, Copy, RotateCcw } from 'lucide-react';
import { CalculatorResult } from '../../../types/worker.types';
import { Card, CardContent, CardHeader, CardTitle, Button } from '../../ui';

interface CalculatorToolProps {
  tool: 'pente' | 'denivele' | 'ngf';
  title: string;
  description: string;
  onResultSave: (result: CalculatorResult) => void;
}

export function CalculatorTool({ tool, title, description, onResultSave }: CalculatorToolProps) {
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [result, setResult] = useState<number | null>(null);
  const [unit, setUnit] = useState<string>('');

  const handleInputChange = (field: string, value: string) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculate = () => {
    const numericInputs: Record<string, number> = {};
    let calculationResult: number | null = null;
    let resultUnit = '';

    // Convertir les inputs en nombres
    Object.entries(inputs).forEach(([key, value]) => {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        numericInputs[key] = num;
      }
    });

    // Calculs selon le type d'outil
    switch (tool) {
      case 'pente':
        if (numericInputs.hauteur && numericInputs.distance) {
          calculationResult = (numericInputs.hauteur / numericInputs.distance) * 100;
          resultUnit = '%';
        }
        break;
      
      case 'denivele':
        if (numericInputs.altitude1 && numericInputs.altitude2) {
          calculationResult = Math.abs(numericInputs.altitude1 - numericInputs.altitude2);
          resultUnit = 'm';
        }
        break;
      
      case 'ngf':
        if (numericInputs.altitudeRelative && numericInputs.referenceNGF) {
          calculationResult = numericInputs.altitudeRelative + numericInputs.referenceNGF;
          resultUnit = 'm NGF';
        }
        break;
    }

    if (calculationResult !== null) {
      setResult(calculationResult);
      setUnit(resultUnit);
      
      // Sauvegarder le résultat
      const resultData: CalculatorResult = {
        id: Date.now().toString(),
        tool,
        inputs: numericInputs,
        result: calculationResult,
        unit: resultUnit,
        timestamp: new Date().toISOString()
      };
      
      onResultSave(resultData);
    }
  };

  const copyResult = async () => {
    if (result !== null) {
      try {
        await navigator.clipboard.writeText(`${result.toFixed(2)} ${unit}`);
      } catch (error) {
        console.error('Erreur lors de la copie:', error);
      }
    }
  };

  const reset = () => {
    setInputs({});
    setResult(null);
    setUnit('');
  };

  const renderInputs = () => {
    switch (tool) {
      case 'pente':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hauteur (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.hauteur || ''}
                onChange={(e) => handleInputChange('hauteur', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 1.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Distance horizontale (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.distance || ''}
                onChange={(e) => handleInputChange('distance', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 10"
              />
            </div>
          </div>
        );
      
      case 'denivele':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altitude 1 (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.altitude1 || ''}
                onChange={(e) => handleInputChange('altitude1', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 120.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altitude 2 (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.altitude2 || ''}
                onChange={(e) => handleInputChange('altitude2', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 115.2"
              />
            </div>
          </div>
        );
      
      case 'ngf':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altitude relative (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.altitudeRelative || ''}
                onChange={(e) => handleInputChange('altitudeRelative', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 25.3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Référence NGF (m)
              </label>
              <input
                type="number"
                step="0.01"
                value={inputs.referenceNGF || ''}
                onChange={(e) => handleInputChange('referenceNGF', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: 100"
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const canCalculate = () => {
    switch (tool) {
      case 'pente':
        return inputs.hauteur && inputs.distance && parseFloat(inputs.distance) !== 0;
      case 'denivele':
        return inputs.altitude1 && inputs.altitude2;
      case 'ngf':
        return inputs.altitudeRelative && inputs.referenceNGF;
      default:
        return false;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Calculator className="h-5 w-5" />
          <span>{title}</span>
        </CardTitle>
        <p className="text-sm text-gray-600">{description}</p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {renderInputs()}
        
        <div className="flex space-x-2">
          <Button
            onClick={calculate}
            disabled={!canCalculate()}
            variant="primary"
            fullWidth
          >
            Calculer
          </Button>
          <Button
            onClick={reset}
            variant="outline"
            icon={RotateCcw}
          >
            Reset
          </Button>
        </div>
        
        {result !== null && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Résultat</p>
                <p className="text-2xl font-bold text-green-900">
                  {result.toFixed(2)} {unit}
                </p>
              </div>
              <Button
                onClick={copyResult}
                variant="outline"
                size="sm"
                icon={Copy}
              >
                Copier
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}